import { type Dayjs, isDayjs } from 'dayjs';
import { useMemo } from 'react';

import type { Nullable } from '~/common/types';

import dayjs from '~/common/config/dayjs';

const useDayjs = () =>
  useMemo(() => {
    const formatDate = (dateString: string, format: string | undefined = 'L') =>
      dayjs(dateString).format(format);

    const isBetweenNullable = (
      date: string,
      startDate?: string | null,
      endDate?: string | null,
    ) => {
      if (!startDate) {
        return false;
      }

      const today = dayjs(date);
      const start = dayjs(startDate);

      if (start.isSameOrBefore(today)) {
        if (!endDate) {
          return true;
        }

        const end = dayjs(endDate);
        if (end.isSameOrAfter(today)) {
          return true;
        }
      }

      return false;
    };

    const isTodayBetweenNullable = (startDate?: string | null, endDate?: string | null) =>
      isBetweenNullable(dayjs().format('YYYY-MM-DD'), startDate, endDate);

    const max = (first: Dayjs, second: Dayjs): Dayjs => (first > second ? first : second);

    const min = (first: Nullable<Dayjs>, second: Nullable<Dayjs>): Dayjs => {
      if (!second) {
        if (first) {
          return first;
        }

        return dayjs('0001-01-01');
      }

      if (!first) {
        return dayjs('0001-01-01');
      }

      return first < second ? first : second;
    };

    // code copied from the BE where it is unit tested
    const hasOverlap = (
      start1: Nullable<Dayjs>,
      end1: Nullable<Dayjs>,
      start2: Nullable<Dayjs>,
      end2: Nullable<Dayjs>,
    ) => {
      if (!start1) {
        if (start2) {
          // start1 null, start2 not null, only overlap when end1 is not null and later than start2, end2 doesn't matter
          if (!end1) {
            return false;
          }
          return end1 >= start2;
        }

        // start1 and start2 are null, -> overlap exists when end1 and end2 both not null
        return !!end1 && !!end2;
      }

      if (!start2) {
        if (!end2) {
          return false;
        }
        // start1 not null, start2 null, only overlap when end2 is not null and later than start1, end2 doesn't matter
        return end2 >= start1;
      }

      // start1 and start2 are not null, overlap exists when end1 and end2 both null
      if (!end1 && !end2) {
        return true;
      }

      return max(start1, start2) <= min(end1, end2);
    };

    const dateRangesOverlap = (
      start1?: Nullable<string> | Nullable<Date>,
      end1?: Nullable<string> | Nullable<Date>,
      start2?: Nullable<string> | Nullable<Date>,
      end2?: Nullable<string> | Nullable<Date>,
    ) => {
      const start1Date = start1 ? dayjs(start1) : null;
      const end1Date = end1 ? dayjs(end1) : null;
      const start2Date = start2 ? dayjs(start2) : null;
      const end2Date = end2 ? dayjs(end2) : null;
      return hasOverlap(start1Date, end1Date, start2Date, end2Date);
    };

    const formatRelativeDateDescription = ({
      dateString,
      outputFormat = 'DD/MM/YYYY',
      withFullDate = false,
      convertToLocal = true,
    }: {
      dateString: string;
      outputFormat?: string;
      withFullDate?: boolean;
      convertToLocal?: boolean;
    }) => {
      let dateObject = dayjs(dateString);

      if (convertToLocal) dateObject = dateObject.local();

      if (withFullDate) {
        return `${dateObject.fromNow()} (${dateObject.format(outputFormat)})`;
      }

      return dateObject.fromNow();
    };

    return {
      dateRangesOverlap,
      dayjs,
      formatDate,
      formatDateTime: (dateString: string) => dayjs(dateString).format('L LT'),
      formatRelativeDate: (dateString: string) => dayjs(dateString).fromNow(),
      formatRelativeDateDescription,
      formatTime: (dateString: string) => dayjs(dateString).format('LT'),
      formatTimeWithSeconds: (dateString: string | Date) =>
        dayjs(dateString).local().utc().format('HH:mm:ss'),
      formatUtcDate: (dateString: string | Date, format = 'L') =>
        dayjs.utc(dateString).local().format(format),
      formatUtcDateTime: (dateString: string, format: string | undefined = undefined) =>
        dayjs.utc(dateString).local().format(format),
      formatUtcTime: (timeString: string) =>
        dayjs.utc(timeString, 'HH:mm:ss').local().format('HH:mm'),
      getUtcDateObject: (dateString: string, inputFormat?: string) =>
        dayjs.utc(dateString, inputFormat),
      getWeekOfYear: (dateString: string) => dayjs(dateString).week(),
      isBetween: (date: string | Date, startDate: string | Date, endDate: string | Date) =>
        dayjs(date).isBetween(startDate, endDate, 'day', '[]'),
      isBetweenNullable,
      isFutureDate: (dateString: string) => dayjs(dateString).isAfter(dayjs()),
      isTodayBetweenNullable,
      localeData: dayjs.localeData(),
      toDateOnlyString: (date: Nullable<string | Date | Dayjs>) => {
        if (!date) {
          return null;
        }
        if (isDayjs(date)) {
          return date.format('YYYY-MM-DD');
        }
        return dayjs(date).format('YYYY-MM-DD');
      },
    } as const;
  }, []);

export default useDayjs;
